import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Gamepad2, Train, Coffee, Cat } from 'lucide-react';

const hobbies = [
  {
    icon: <Gamepad2 className="w-8 h-8" />,
    title: "Gaming",
    desc: "Abschalten in virtuellen Welten. Simulationen und Strategie sind meine Favoriten."
  },
  {
    icon: <Camera className="w-8 h-8" />,
    title: "Fotografie",
    desc: "Einfangen von Stimmungen. Vor allem im Bereich der Eisenbahn."
  },
  {
    icon: <Coffee className="w-8 h-8" />,
    title: "Kaffee",
    desc: "Der Treibstoff für Nachtschichten und Coding-Sessions. Ohne geht nichts."
  },
  {
    icon: <Cat className="w-8 h-8" />,
    title: "Katzen",
    desc: "Office-Mates & Seelentröster. Finja, Mio & Naomi sind immer dabei."
  }
];

const Hobbies = () => {
  return (
    <section id="hobbies" className="py-24 bg-background">
      <div className="container mx-auto px-4">

        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">AUSGLEICH ZUM <span className="text-primary">ALLTAG</span></h2>
          <div className="h-1 w-20 bg-primary mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {hobbies.map((hobby, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative p-8 border border-border bg-card/50 hover:bg-card hover:border-primary/30 transition-all duration-300 rounded-sm text-center"
            >
              <div className="mb-6 inline-flex p-4 rounded-full bg-secondary/20 text-foreground group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                {hobby.icon}
              </div>
              <h3 className="text-xl font-display font-bold mb-3">{hobby.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {hobby.desc}
              </p>

              {/* Corner Accents */}
              <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-primary/50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-primary/50 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </motion.div>
          ))}
        </div>

        {/* Big Image Banner */}
        {/* Big Image Banner Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-24">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative rounded-sm overflow-hidden h-64 md:h-80 border border-border group"
          >
            <img
              src="/assets/images/railway_yard.webp"
              alt="Railway Photography - Yard"
              className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <h3 className="text-2xl md:text-3xl font-display text-white tracking-widest uppercase border-4 border-white/20 p-4 backdrop-blur-sm">
                LOGISTIK
              </h3>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative rounded-sm overflow-hidden h-64 md:h-80 border border-border group"
          >
            <img
              src="/assets/images/blackforest_cab.webp"
              alt="Railway Photography - Black Forest Cab View"
              className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <h3 className="text-2xl md:text-3xl font-display text-white tracking-widest uppercase border-4 border-white/20 p-4 backdrop-blur-sm">
                SCHWARZWALD
              </h3>
            </div>
          </motion.div>
        </div>

        {/* Cat Gallery */}
        <div className="mt-24">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-display font-bold">MEINE CREW</h3>
            <p className="text-muted-foreground mt-2">Die wahren Chefs im Haus.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { name: "Finja", img: "/assets/images/cat_finja.webp" },
              { name: "Mio", img: "/assets/images/cat_mio.webp" },
              { name: "Naomi", img: "/assets/images/cat_naomi.webp" },
              { name: "Lili", img: "/assets/images/cat_lili.webp", isMemorial: true, hoverLabel: "Lili - IN MEMORIES" }
            ].map((cat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="group relative aspect-square rounded-sm overflow-hidden border border-border/50"
              >
                <img
                  src={cat.img}
                  alt={cat.name}
                  className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${cat.isMemorial ? 'grayscale opacity-80' : ''}`}
                />
                <div className="absolute inset-0 flex items-end justify-center pb-6 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white font-display text-xl tracking-wider">{cat.hoverLabel || cat.name.toUpperCase()}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Hobbies;
